import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { AdminMe } from "@vital/shared";
import { canPerform } from "@vital/shared";
import { VT_BP_DRAWER_PX } from "../config/breakpoints";
import { NAV_SECTIONS, isNavItemActive } from "../config/nav";
import { resolveRouteMeta } from "../config/route-meta";
import { cmsStrings } from "../terminology";
import { AuthBrand } from "./AuthBrand";
import { Breadcrumb } from "./Breadcrumb";
import { LtrIsolate } from "./LtrIsolate";
import { SidebarUserCard } from "./SidebarUserCard";
import { SystemSearch } from "./SystemSearch";
import { TenantContextBar, TenantContextBanners } from "./TenantContextBar";

type Props = {
  user: AdminMe;
  onLogout: () => void;
  children: ReactNode;
};

const COLLAPSE_KEY = "vital-cms-nav-collapsed";

function loadCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    return {};
  }
}

export function DashboardLayout({ user, onLogout, children }: Props) {
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsed);
  const drawerRef = useRef<HTMLElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const brandTitleId = useId();

  const navSections = useMemo(() => {
    return NAV_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.permission) return true;
        return canPerform({
          platformRole: user.platformRole ?? null,
          membershipRole: user.membershipRole ?? null,
          legacyRole: user.role,
          permission: item.permission,
        });
      }),
    })).filter((section) => section.items.length > 0);
  }, [user.role, user.platformRole, user.membershipRole]);

  const meta = resolveRouteMeta(location.pathname, location.search);
  const isDev = import.meta.env.DEV;

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    if (!navOpen) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia(`(min-width: ${VT_BP_DRAWER_PX + 1}px)`).matches
    ) {
      return;
    }

    const drawer = drawerRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusables = () =>
      drawer
        ? Array.from(
            drawer.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          )
        : [];

    focusables()[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setNavOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (e.key !== "Tab" || !drawer) return;
      const list = focusables();
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [navOpen]);

  function toggleSection(id: string) {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div
      className={`app-shell${navOpen ? " app-shell--nav-open" : ""}`}
      data-vt-shell="m1"
    >
      <a href="#main-content" className="skip-link">
        {cmsStrings.shell.skipToContent}
      </a>

      {navOpen && (
        <div
          className="nav-drawer-backdrop"
          role="presentation"
          data-vt-nav-backdrop
          onClick={() => setNavOpen(false)}
        />
      )}

      <aside
        ref={drawerRef}
        className="sidebar"
        data-vt-nav-rail
        aria-label={cmsStrings.shell.mainMenuAria}
        {...(navOpen
          ? {
              role: "dialog",
              "aria-modal": true as const,
              "aria-labelledby": brandTitleId,
            }
          : {})}
      >
        <div className="sidebar-drawer-header">
          <button
            type="button"
            className="btn btn--ghost btn--sm nav-drawer-close"
            data-vt-nav-close
            onClick={() => {
              setNavOpen(false);
              menuButtonRef.current?.focus();
            }}
          >
            {cmsStrings.shell.closeMenu}
          </button>
        </div>

        <AuthBrand variant="rail" titleId={brandTitleId} />

        <nav className="sidebar-nav" data-vt-nav-sections>
          {navSections.map((section) => {
            const isCollapsed = Boolean(collapsed[section.id]);
            return (
              <div key={section.id} className="nav-section" data-nav-section={section.id}>
                <button
                  type="button"
                  className="nav-section__toggle"
                  aria-expanded={!isCollapsed}
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="nav-section__title">{section.title}</span>
                  <span className="nav-section__chevron" aria-hidden="true">
                    {isCollapsed ? "+" : "−"}
                  </span>
                </button>
                {!isCollapsed &&
                  section.items.map((item) => {
                    const active = isNavItemActive(
                      item,
                      location.pathname,
                      location.search,
                    );
                    const classes = [
                      "nav-link",
                      active ? "nav-link--active" : "",
                      item.nested ? "nav-link--nested" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={classes}
                        aria-current={active ? "page" : undefined}
                      >
                        {item.label}
                      </NavLink>
                    );
                  })}
              </div>
            );
          })}
        </nav>

        <SidebarUserCard user={user} onLogout={onLogout} />
      </aside>

      <div className="main-column">
        <div className="main-inner">
          <header className="topbar" data-vt-context-bar>
            <div className="topbar__start">
              <button
                ref={menuButtonRef}
                type="button"
                className="nav-menu-button"
                data-vt-nav-open
                aria-expanded={navOpen}
                aria-label={cmsStrings.shell.openMenuAria}
                onClick={() => setNavOpen((o) => !o)}
              >
                {cmsStrings.shell.openMenu}
              </button>
              <div className="topbar__org" data-vt-topbar-org>
                <TenantContextBar />
              </div>
            </div>
            <div className="topbar__tools">
              <SystemSearch user={user} />
              <p className="topbar-meta">
                {cmsStrings.shell.displayTimezoneLabel}{" "}
                <LtrIsolate>{cmsStrings.shell.displayTimezoneValue}</LtrIsolate>
              </p>
              {isDev && <span className="env-badge">{cmsStrings.shell.envDevBadge}</span>}
            </div>
          </header>

          <div className="shell-context">
            <Breadcrumb items={[{ label: meta.group }, { label: meta.title }]} />
            {meta.explanation && (
              <p className="shell-context__hint">{meta.explanation}</p>
            )}
          </div>

          <main id="main-content" className="main-content" tabIndex={-1}>
            <TenantContextBanners />
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
